import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Lock, X } from "lucide-react";

import { Avatar } from "../../components/Avatar/Avatar";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { useToast } from "../../components/Toast/toastContext";
import { apiClient } from "../../lib/apiClient";
import { ApiError, describeError } from "../../lib/errors";
import { AuthNotice } from "../auth/AuthNotice";
import { BrandMark } from "../auth/BrandMark";
import { meQueryOptions } from "../auth/useAuth";
import auth from "../auth/AuthLayout.module.css";

import {
  type AuthorizeDecision,
  type AuthorizeRequest,
  decideAuthorizeRequest,
  getAuthorizeRequest,
} from "./api";
import type { DoneState } from "./doneState";
import { EnvironmentBadge } from "./EnvironmentBadge";
import { clientInitial } from "./format";
import styles from "./AuthorizePage.module.css";

type View =
  | { kind: "loading" }
  | { kind: "invalid"; code: string | null }
  | { kind: "forbidden"; email: string }
  | { kind: "consent"; request: AuthorizeRequest };

type Busy = AuthorizeDecision | "switch" | null;

function loginPath(next: string): string {
  return `/login?next=${encodeURIComponent(next)}`;
}

export function AuthorizePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { push } = useToast();
  const search = location.search;
  const here = location.pathname + search;

  // Keyed by the query it answers, so a new request reads as loading rather
  // than showing the previous client's screen.
  const [loaded, setLoaded] = useState<{ search: string; view: View } | null>(
    null,
  );
  const view: View =
    loaded && loaded.search === search ? loaded.view : { kind: "loading" };
  const [busy, setBusy] = useState<Busy>(null);

  useEffect(() => {
    let cancelled = false;
    getAuthorizeRequest(search)
      .then((request) => {
        if (cancelled) return;
        setLoaded({
          search,
          view: request.allowed
            ? { kind: "consent", request }
            : { kind: "forbidden", email: request.user.email },
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          navigate(loginPath(here), { replace: true });
          return;
        }
        setLoaded({
          search,
          view: {
            kind: "invalid",
            code: err instanceof ApiError ? err.code : null,
          },
        });
      });
    return () => {
      cancelled = true;
    };
  }, [search, here, navigate]);

  // This page sits outside AuthGuard, which is where the CSRF token is
  // normally put on the client; a fresh /auth/me puts the current session's.
  async function refreshCSRF() {
    await qc.fetchQuery({ ...meQueryOptions, staleTime: 0 });
  }

  async function decide(decision: AuthorizeDecision, request: AuthorizeRequest) {
    setBusy(decision);
    try {
      await refreshCSRF();
      const redirectUrl = await decideAuthorizeRequest(
        search.replace(/^\?/, ""),
        decision,
      );
      const state: DoneState = {
        kind: decision === "approve" ? "connected" : "cancelled",
        clientName: request.client.name,
        redirectUrl,
      };
      navigate("/oauth/done", { replace: true, state });
    } catch (err) {
      setBusy(null);
      if (err instanceof ApiError && err.status === 401) {
        navigate(loginPath(here), { replace: true });
      } else if (err instanceof ApiError && err.code === "forbidden") {
        setLoaded({
          search,
          view: { kind: "forbidden", email: request.user.email },
        });
      } else if (err instanceof ApiError && err.status === 400) {
        setLoaded({ search, view: { kind: "invalid", code: err.code } });
      } else {
        push({
          variant: "danger",
          message:
            err instanceof ApiError
              ? describeError(err.code)
              : describeError("internal"),
        });
      }
    }
  }

  async function switchAccount() {
    setBusy("switch");
    try {
      await refreshCSRF();
      await apiClient.post("/api/admin/auth/logout");
    } catch {
      // Already signed out, or the call failed — sign-in follows either way.
    }
    // Everything cached so far belongs to the account being left.
    qc.clear();
    navigate(loginPath(here), { replace: true });
  }

  if (view.kind === "loading") {
    return (
      <div className={auth.page}>
        <div className={`${auth.card} ${auth.cardWide}`}>
          <BrandMark />
          <div className={auth.subtitle}>Loading the request…</div>
        </div>
      </div>
    );
  }

  if (view.kind === "invalid") {
    return (
      <div className={auth.page}>
        <div className={`${auth.card} ${auth.hero}`}>
          <div className={`${auth.heroIcon} ${auth.heroIconWarn}`}>
            <AlertTriangle size={30} aria-hidden="true" />
          </div>
          <h1 className={auth.title}>This request can&apos;t be completed</h1>
          <div className={styles.heroSub}>
            The app sent an authorization request Social Hub doesn&apos;t
            accept. Start the connection again from the app.
          </div>
          {view.code && (
            <div className={`${auth.infoBox} ${styles.infoBox}`}>
              <div>
                Reason: <code>{view.code}</code>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (view.kind === "forbidden") {
    return (
      <div className={auth.page}>
        <div className={`${auth.card} ${auth.hero}`}>
          <div className={`${auth.heroIcon} ${auth.heroIconDanger}`}>
            <Lock size={30} aria-hidden="true" />
          </div>
          <h1 className={auth.title}>Super admin access required</h1>
          <div className={`${styles.heroSub} ${styles.heroSubBeforeButtons}`}>
            MCP access is limited to super admins. You&apos;re signed in as{" "}
            <b>{view.email}</b>.
          </div>
          <div className={`${styles.buttons} ${styles.buttonsFlush}`}>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => void switchAccount()}
              loading={busy === "switch"}
            >
              Switch account
            </Button>
            <Button
              size="lg"
              onClick={() => navigate("/projects")}
              disabled={busy !== null}
            >
              Back to admin
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { request } = view;
  const { client, user } = request;
  return (
    <div className={auth.page}>
      <div className={`${auth.card} ${auth.cardWide}`}>
        <BrandMark />
        <h1 className={auth.title}>Authorize {client.name}</h1>
        <div className={auth.subtitle}>
          This app wants read-only access to this environment.
        </div>
        <div className={styles.envRow}>
          <EnvironmentBadge environment={request.environment} />
        </div>
        <div className={styles.client}>
          <div className={styles.clientLogo} aria-hidden="true">
            {clientInitial(client.name)}
          </div>
          <div className={styles.clientText}>
            <div className={styles.clientName}>{client.name}</div>
            <div className={styles.clientMeta}>
              {client.host ? `${client.host} · ` : ""}redirects to{" "}
              {client.redirect_host}
            </div>
          </div>
        </div>
        <p className={styles.permHeading}>It will be able to</p>
        <ul className={styles.perms}>
          <li className={styles.yes}>
            <span className={styles.permIcon} aria-hidden="true">
              <Check size={11} strokeWidth={3} />
            </span>
            <div>
              Run read-only SQL queries
              <small>
                Every table in the Postgres database, including player data
              </small>
            </div>
          </li>
          <li className={styles.yes}>
            <span className={styles.permIcon} aria-hidden="true">
              <Check size={11} strokeWidth={3} />
            </span>
            <div>
              Read Redis keys
              <small>Presence, config cache and the leaderboard cache</small>
            </div>
          </li>
          <li className={styles.no}>
            <span className={styles.permIcon} aria-hidden="true">
              <X size={10} strokeWidth={3} />
            </span>
            <div>Change, delete or write anything</div>
          </li>
        </ul>
        {request.environment === "production" && (
          <AuthNotice variant="warn" className={styles.warn}>
            Production holds players&apos; personal data. Only authorize apps
            you run yourself.
          </AuthNotice>
        )}
        <div className={styles.who}>
          <Avatar name={user.name} size={28} variant="neutral" />
          <div className={styles.whoText}>
            <div>
              {user.name}
              <span className={styles.whoBadge}>
                <Badge variant="admin">Super admin</Badge>
              </span>
            </div>
            <div className={styles.whoEmail}>{user.email}</div>
          </div>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => void switchAccount()}
            disabled={busy !== null}
          >
            Switch account
          </button>
        </div>
        <div className={styles.buttons}>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => void decide("deny", request)}
            disabled={busy !== null}
            loading={busy === "deny"}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            onClick={() => void decide("approve", request)}
            disabled={busy !== null}
            loading={busy === "approve"}
          >
            Authorize
          </Button>
        </div>
      </div>
    </div>
  );
}
